import type { QueryClientProviderProps } from '@tanstack/react-query';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { t } from 'i18next';
import { useContext, useMemo } from 'react';
import { isCustomError } from '../base';
import { notification } from '../components';
import type { ProviderTuple } from './types';
import { LoggerContext } from './LoggerProvider';

const parseError = (error: unknown) => {
  if (isCustomError(error)) {
    return {
      message: error.message,
    };
  }

  if (error instanceof Error) {
    return {
      message: t('Errors.DocumentUnhandledDescription'),
      description: error.message,
    };
  }

  return { message: t('Errors.DocumentUnhandledDescription') };
};

// TODO: need to add global error handle to wrap errors to custom errors interface
// Fast solution
const onErrorNotification = (error: unknown) => {
  notification.error(parseError(error));
};

export const QueryProvider: ProviderTuple<
  Omit<QueryClientProviderProps, 'client' | 'contextSharing'>
> = [
  function WithLogger(props) {
    const loggerService = useContext(LoggerContext);

    const queryClient = useMemo(
      () =>
        new QueryClient({
          mutationCache: new MutationCache({
            onError: (error, _variables, _context, mutation) => {
              if (mutation.options.onError) return;
              onErrorNotification(error);
              loggerService.error(error);
            },
          }),
          queryCache: new QueryCache({
            onError: error => {
              onErrorNotification(error);
              loggerService.error(error);
            },
          }),
        }),
      [loggerService],
    );

    return <QueryClientProvider client={queryClient} {...props} />;
  },
];
